///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    Signal,
    SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { ProjectDragAndDropService } from '../project-drag-and-drop.service';
import { E2eTestUtils } from '../../../testing/e2e-test-utils';
import { ProjectDTO } from '../../generated/shared-types.model';
import { ProjectListViewType } from '../project-enums';
import { Principal } from '../../shared/auth/principal.service';
import { UserAuthorities } from '../../services/user/user-authorities';
import { ProjectsViewStateService } from '../projects-view-state.service';

@Component({
    selector: 'winde-projects-list',
    templateUrl: './projects-list.component.html',
    styleUrls: ['./projects-list.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthProjectsListComponent implements OnInit, OnChanges {
    public readonly tooltipsAreDisabled = E2eTestUtils.isTooltipsDisabled();
    public isDragAllowed: Promise<boolean>;
    public searchFilter: Signal<string>;
    public isTableViewTypeSelected: Signal<boolean>;

    @Input() projects: ProjectDTO[];
    @Input() sortParam = 'name';
    @Input() sortDescending = false;

    @Output() sortParamChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() sortDescendingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(
        private readonly router: Router,
        private readonly principal: Principal,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly projectsViewStateService: ProjectsViewStateService,
        public readonly projectDragAndDropService: ProjectDragAndDropService
    ) {
        this.searchFilter = this.projectsViewStateService.searchFilter;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.projects?.currentValue) {
            this.changeSort(this.sortParam, false);
        }
    }

    ngOnInit(): void {
        this.isDragAllowed = this.principal.hasAnyAuthority(UserAuthorities.PROJECT_EDIT);
        this.isTableViewTypeSelected = computed(() => this.projectsViewStateService.currentViewType() === ProjectListViewType.TABLE);
    }

    public changeSort(param: string, switchDirection = true) {
        if (switchDirection && this.sortParam === param) {
            this.sortDescending = !this.sortDescending;
            this.sortDescendingChange.emit(this.sortDescending);
        } else if (this.sortParam !== param) {
            this.sortParam = param;
            this.sortParamChange.emit(this.sortParam);
        }

        if (this.sortParam === 'lastEditDate') {
            this.projects.sort((a, b) => a.lastEditDate.getTime() - b.lastEditDate.getTime());
        } else {
            this.projects.sort((a, b) => b[this.sortParam] - a[this.sortParam]);
        }

        this.changeDetector.markForCheck();
    }

    public viewProject(project: ProjectDTO) {
        this.router.navigate(['./project-view', project.id]);
    }
}
